import React, { useState } from "react";
import { Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BANGLE_MEASURE_STEPS, RING_MEASURE_STEPS, nearestBangleSize, nearestRingSize } from "@/lib/sizeGuide";
import { BangleSizeTable, RingSizeTable } from "@/components/product/SizeTables";

const tabClass =
  "rounded-none border-b-2 border-transparent px-4 py-3 text-[11px] uppercase tracking-[0.18em] data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-gold data-[state=active]:shadow-none";
const panelClass = "mt-0 overflow-y-auto px-6 py-6 sm:px-8";

function FitFinder({ label, placeholder, onFind, testId }) {
  const [value, setValue] = useState("");
  const result = onFind(parseFloat(value));
  return (
    <div className="border border-gold/40 bg-gold-light/60 p-4 sm:p-5">
      <p className="text-luxe text-[10px] text-gold">Find my size</p>
      <label className="mt-2 block text-sm text-ink">{label}</label>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="number"
          inputMode="decimal"
          min="30"
          max="260"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className="h-11 w-36 border border-border bg-white px-3 text-sm text-ink outline-none focus:border-gold"
        />
        <span className="text-xs text-muted-foreground">mm</span>
      </div>
      {result && (
        <p className="mt-3 text-sm text-ink" data-testid={`${testId}-result`}>
          Your closest fit is <span className="font-serif text-lg text-gold">{result.in ? `Indian ${result.in}` : `Size ${result.size}`}</span>
          {result.us && <span className="text-muted-foreground"> · US {result.us} · UK {result.uk}</span>}
          {result.diaIn && <span className="text-muted-foreground"> · {result.diaIn} inner diameter</span>}
        </p>
      )}
    </div>
  );
}

function Steps({ title, steps }) {
  return (
    <div>
      <p className="text-luxe text-[10px] text-gold mb-4">{title}</p>
      <ol className="space-y-5">
        {steps.map((s, i) => (
          <li key={s.title} className="flex gap-4">
            <span className="grid h-8 w-8 flex-none place-items-center rounded-full border border-gold font-serif text-sm text-gold">{i + 1}</span>
            <div>
              <p className="font-serif text-lg text-ink">{s.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SizeGuideTabs({ defaultTab }) {
  return (
    <Tabs defaultValue={defaultTab} className="flex max-h-[calc(90vh-130px)] flex-col">
      <TabsList className="flex w-full justify-start gap-1 rounded-none border-b border-border bg-transparent px-6 py-0 sm:px-8">
        <TabsTrigger value="rings" className={tabClass} data-testid="size-guide-tab-rings">Rings</TabsTrigger>
        <TabsTrigger value="bangles" className={tabClass} data-testid="size-guide-tab-bangles">Bangles</TabsTrigger>
        <TabsTrigger value="measure" className={tabClass} data-testid="size-guide-tab-measure">How to Measure</TabsTrigger>
      </TabsList>
      <TabsContent value="rings" className={panelClass}>
        <FitFinder label="Inside circumference of your finger" placeholder="e.g. 54" onFind={nearestRingSize} testId="size-guide-ring-fit" />
        <RingSizeTable />
      </TabsContent>
      <TabsContent value="bangles" className={panelClass}>
        <FitFinder label="Circumference around the widest part of your hand" placeholder="e.g. 185" onFind={nearestBangleSize} testId="size-guide-bangle-fit" />
        <BangleSizeTable />
      </TabsContent>
      <TabsContent value="measure" className={panelClass}>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          <Steps title="Rings" steps={RING_MEASURE_STEPS} />
          <Steps title="Bangles" steps={BANGLE_MEASURE_STEPS} />
        </div>
      </TabsContent>
    </Tabs>
  );
}

export const needsSizeGuide = (product) => /ring|bangle/i.test(product?.category || "");

export function SizeGuide({ category = "", className = "" }) {
  const [open, setOpen] = useState(false);
  const defaultTab = /bangle/i.test(category) ? "bangles" : "rings";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="size-guide-trigger"
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground underline-offset-4 transition-colors hover:text-gold hover:underline ${className}`}
      >
        <Ruler size={13} /> Size Guide
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden border-border bg-ivory p-0 sm:rounded-none" data-testid="size-guide-dialog">
          <div className="border-b border-border px-6 pb-4 pt-7 sm:px-8">
            <p className="text-luxe text-[10px] text-gold">Fit Guide</p>
            <DialogTitle className="mt-1 font-serif text-3xl font-light text-ink">Find Your Perfect Fit</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              Indian sizing with international equivalents. Unsure between two sizes? Our concierge is happy to help.
            </DialogDescription>
          </div>
          <SizeGuideTabs defaultTab={defaultTab} />
        </DialogContent>
      </Dialog>
    </>
  );
}
